import { useState } from "react";
import {
  Sparkles, Loader2, Share2, Pin, PinOff, Bookmark, BookmarkCheck,
  ChevronDown, PlusCircle, X, ArchiveRestore, FileText
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ReactionBar } from "@/components/ReactionBar";
import { MessageSpeaker } from "@/components/MessageSpeaker";
import { VoiceOnboarding } from "@/components/VoiceOnboarding";
import { CalendarWidget } from "@/components/CalendarWidget";

const MOOD_EMOJIS = [
  { emoji: "\u{1F60A}", label: "Great", score: 9, emotions: ["happy", "great"] },
  { emoji: "\u{1F60C}", label: "Good", score: 7, emotions: ["calm", "peaceful"] },
  { emoji: "\u{1F610}", label: "Okay", score: 5, emotions: ["neutral"] },
  { emoji: "\u{1F614}", label: "Low", score: 3, emotions: ["sad", "tired"] },
  { emoji: "\u{1F622}", label: "Rough", score: 1, emotions: ["overwhelmed", "stressed"] },
];

import { Heart, Briefcase, Brain, Target, BookOpen, Users, Sparkles as SparkIcon, Flame, Sun, Shield, Lightbulb, Coffee, Compass, MessageCircle } from "lucide-react";

const TEMPLATE_CATEGORIES = [
  { key: "daily", label: "Daily Check-in", icon: Sun },
  { key: "growth", label: "Personal Growth", icon: Lightbulb },
  { key: "career", label: "Career & Work", icon: Briefcase },
  { key: "emotional", label: "Emotional Support", icon: Heart },
  { key: "relationships", label: "Relationships", icon: Users },
  { key: "purpose", label: "Life & Purpose", icon: Compass },
];

const QUICK_TEMPLATES = [
  // Daily Check-in
  { text: "Process my day", desc: "Unpack what happened today", module: "friend", icon: Sun, category: "daily", color: "bg-amber-50 dark:bg-amber-950/30 border-amber-200/50 dark:border-amber-800/30" },
  { text: "What should I focus on today?", desc: "Set clear priorities", module: "life_coach", icon: Target, category: "daily", color: "bg-orange-50 dark:bg-orange-950/30 border-orange-200/50 dark:border-orange-800/30" },
  { text: "I need a morning pep talk", desc: "Start the day strong", module: "mentor", icon: Flame, category: "daily", color: "bg-red-50 dark:bg-red-950/30 border-red-200/50 dark:border-red-800/30" },

  // Personal Growth
  { text: "Help me set a new goal", desc: "Break it into steps", module: "life_coach", icon: Target, category: "growth", color: "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200/50 dark:border-emerald-800/30" },
  { text: "I want to build a new habit", desc: "Make it stick", module: "life_coach", icon: SparkIcon, category: "growth", color: "bg-teal-50 dark:bg-teal-950/30 border-teal-200/50 dark:border-teal-800/30" },
  { text: "What's holding me back?", desc: "Identify your blind spots", module: "mentor", icon: Brain, category: "growth", color: "bg-violet-50 dark:bg-violet-950/30 border-violet-200/50 dark:border-violet-800/30" },

  // Career & Work
  { text: "Prep me for an interview", desc: "Nail that opportunity", module: "job_guardian", icon: Briefcase, category: "career", color: "bg-purple-50 dark:bg-purple-950/30 border-purple-200/50 dark:border-purple-800/30" },
  { text: "How do I ask for a raise?", desc: "Build your case", module: "job_guardian", icon: Shield, category: "career", color: "bg-indigo-50 dark:bg-indigo-950/30 border-indigo-200/50 dark:border-indigo-800/30" },
  { text: "I'm burned out at work", desc: "Find your way through", module: "mentor", icon: Coffee, category: "career", color: "bg-stone-50 dark:bg-stone-950/30 border-stone-200/50 dark:border-stone-800/30" },

  // Emotional Support
  { text: "Guide me through anxiety", desc: "Ground yourself", module: "friend", icon: Brain, category: "emotional", color: "bg-blue-50 dark:bg-blue-950/30 border-blue-200/50 dark:border-blue-800/30" },
  { text: "I'm feeling lonely today", desc: "You're not alone", module: "friend", icon: Heart, category: "emotional", color: "bg-pink-50 dark:bg-pink-950/30 border-pink-200/50 dark:border-pink-800/30" },
  { text: "Help me process grief", desc: "Heal at your pace", module: "friend", icon: Heart, category: "emotional", color: "bg-rose-50 dark:bg-rose-950/30 border-rose-200/50 dark:border-rose-800/30" },

  // Relationships
  { text: "How do I handle this conflict?", desc: "Navigate disagreements", module: "social_guide", icon: Users, category: "relationships", color: "bg-cyan-50 dark:bg-cyan-950/30 border-cyan-200/50 dark:border-cyan-800/30" },
  { text: "I need to set boundaries", desc: "Protect your peace", module: "social_guide", icon: Shield, category: "relationships", color: "bg-sky-50 dark:bg-sky-950/30 border-sky-200/50 dark:border-sky-800/30" },
  { text: "How do I apologize well?", desc: "Repair what matters", module: "social_guide", icon: MessageCircle, category: "relationships", color: "bg-lime-50 dark:bg-lime-950/30 border-lime-200/50 dark:border-lime-800/30" },

  // Life & Purpose
  { text: "What gives life meaning?", desc: "The big questions", module: "mentor", icon: Compass, category: "purpose", color: "bg-amber-50 dark:bg-amber-950/30 border-amber-200/50 dark:border-amber-800/30" },
  { text: "Tell me a story with a moral", desc: "Wisdom through narrative", module: "storyteller", icon: BookOpen, category: "purpose", color: "bg-fuchsia-50 dark:bg-fuchsia-950/30 border-fuchsia-200/50 dark:border-fuchsia-800/30" },
  { text: "I'm at a crossroads in life", desc: "Find your direction", module: "life_coach", icon: Compass, category: "purpose", color: "bg-yellow-50 dark:bg-yellow-950/30 border-yellow-200/50 dark:border-yellow-800/30" },
];

const ICON_MAP = { Heart, Briefcase, Brain, Target, BookOpen, Users, Sun, Shield, Lightbulb, Coffee, Compass, MessageCircle, Flame, SparkIcon };

function QuickStartTemplates({ applyTemplate }) {
  const [activeCategory, setActiveCategory] = useState("daily");
  const filtered = QUICK_TEMPLATES.filter(t => t.category === activeCategory);
  const MODULE_LABELS = { friend: "Friend", psychologist: "Psychologist", mentor: "Mentor", social_guide: "Social", job_guardian: "Guardian", life_coach: "Coach", storyteller: "Storyteller" };

  return (
    <div className="w-full" data-testid="quick-start-templates">
      {/* Category tabs — horizontally scrollable */}
      <div className="flex items-center gap-1 mb-2.5 overflow-x-auto scrollbar-none pb-0.5 -mx-1 px-1">
        {TEMPLATE_CATEGORIES.map(cat => {
          const CatIcon = cat.icon;
          return (
            <button
              key={cat.key}
              data-testid={`tpl-cat-${cat.key}`}
              onClick={() => setActiveCategory(cat.key)}
              className={cn(
                "flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[10px] font-medium whitespace-nowrap transition-all flex-shrink-0",
                activeCategory === cat.key
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "bg-muted/40 text-muted-foreground hover:bg-muted/60 hover:text-foreground"
              )}
            >
              <CatIcon className="w-3 h-3" strokeWidth={2} />
              {cat.label}
            </button>
          );
        })}
      </div>

      {/* Template cards for active category */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 animate-in fade-in duration-200">
        {filtered.map((tpl, i) => {
          const TplIcon = tpl.icon;
          return (
            <button
              key={`${tpl.category}-${i}`}
              data-testid={`template-${tpl.category}-${i}`}
              onClick={() => applyTemplate({ ...tpl })}
              className={cn(
                "flex items-start gap-2.5 p-3 rounded-xl border text-left transition-all",
                "hover:shadow-md hover:-translate-y-0.5 active:scale-[0.97]",
                tpl.color
              )}
            >
              {TplIcon && (
                <div className="w-7 h-7 rounded-lg bg-white/50 dark:bg-white/5 flex items-center justify-center flex-shrink-0">
                  <TplIcon className="w-3.5 h-3.5 text-foreground/60" strokeWidth={1.5} />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-foreground/85 leading-snug">{tpl.text}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1">
                  {tpl.desc}
                  <span className="text-[9px] px-1.5 py-0 rounded-full bg-black/5 dark:bg-white/5">{MODULE_LABELS[tpl.module]}</span>
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function ChatMessageList({
  t, name,
  messages, activeConv, module, sending, bulkProgress,
  resumeSession, messagesEndRef, messageAreaRef, zoomLevel, setZoomLevel,
  isOnline, offlineQueue, flushing,
  // Pinned
  pinnedMsgs, showPinned, setShowPinned,
  // Reactions & bookmarks
  reactionsMap, bookmarkedMsgs, handleReactionChange, toggleBookmark, togglePinMessage,
  // Smart replies
  smartReplies, loadingReplies, sendSmartReply,
  // Mood
  moodNoteFor, moodNoteInput, setMoodNoteInput, quickMoodLog, submitMoodWithNote, skipMoodNote,
  // Templates
  customTemplates, showNewTemplate, setShowNewTemplate, newTemplateText, setNewTemplateText,
  saveTemplate, deleteTemplate, applyTemplate, inputRef,
  // Voice
  showVoiceOnboarding, setShowVoiceOnboarding,
  // Session
  resumeLastSession, dismissResume,
  // Share
  setShareMsg, setInput,
  // Drafts
  draftConvIds, conversations, selectConversation,
  // Wisdom
  onOpenWisdomCouncil, activeWisdomFigure,
}) {
  return (
    <>
      {/* Offline Indicator Banner */}
      {!isOnline && (
        <div
          data-testid="offline-banner"
          className={cn(
            "mx-2 sm:mx-4 md:mx-8 mt-2 px-4 py-2.5 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center gap-2.5 animate-in fade-in slide-in-from-top-2 duration-200",
            flushing && "animate-pulse"
          )}
        >
          <div className={cn("w-2 h-2 rounded-full flex-shrink-0", flushing ? "bg-green-500 animate-ping" : "bg-amber-500 animate-pulse")} />
          <span className="text-xs sm:text-sm text-amber-700 dark:text-amber-400 font-medium">
            {flushing
              ? "Reconnected — sending queued messages..."
              : "You're offline \u2014 messages will be queued and sent when you reconnect"
            }
          </span>
          {offlineQueue.length > 0 && !flushing && (
            <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-300 font-medium">
              {offlineQueue.length} queued
            </span>
          )}
        </div>
      )}

      {/* Sync banner — shows briefly when flushing while online */}
      {isOnline && flushing && (
        <div
          data-testid="sync-banner"
          className="mx-2 sm:mx-4 md:mx-8 mt-2 px-4 py-2.5 rounded-xl bg-green-500/10 border border-green-500/30 flex items-center gap-2.5 animate-in fade-in slide-in-from-top-2 duration-200"
        >
          <div className="w-2 h-2 rounded-full bg-green-500 animate-ping flex-shrink-0" />
          <span className="text-xs sm:text-sm text-green-700 dark:text-green-400 font-medium">
            Sending queued messages...
          </span>
          <Loader2 className="w-3.5 h-3.5 animate-spin text-green-600 ml-auto" />
        </div>
      )}

      {/* Messages — pinch-to-zoom enabled */}
      <div
        ref={messageAreaRef}
        className="flex-1 overflow-y-auto overflow-x-hidden px-2 sm:px-4 md:px-8 py-4 sm:py-6 space-y-4 sm:space-y-6"
        style={{ touchAction: "pan-y pinch-zoom", transform: `scale(${zoomLevel})`, transformOrigin: "top center", transition: "transform 0.1s ease-out" }}
      >
        {/* Session Resume Card — prominent */}
        {resumeSession && !activeConv && messages.length === 0 && (
          <div
            data-testid="session-resume-card"
            className="max-w-md w-full mx-auto bg-gradient-to-br from-primary/5 to-primary/10 border-2 border-primary/25 rounded-2xl p-4 sm:p-5 shadow-md animate-in fade-in slide-in-from-top-2 duration-300"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center">
                <ArchiveRestore className="w-5 h-5 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">Pick up where you left off</p>
                <p className="text-xs text-muted-foreground truncate">
                  {resumeSession.conversation_title || "Your last session"} — {resumeSession.module || "Chat"}
                </p>
              </div>
            </div>
            {resumeSession.draft_text && (
              <div data-testid="resume-draft-preview" className="mb-3 p-2.5 bg-background/60 rounded-xl border border-border/40">
                <div className="flex items-center gap-1.5 mb-1">
                  <FileText className="w-3 h-3 text-primary" />
                  <span className="text-[10px] font-medium text-primary ui-label">Unsent draft</span>
                </div>
                <p className="text-xs text-foreground/70 line-clamp-2 italic">&ldquo;{resumeSession.draft_text}&rdquo;</p>
              </div>
            )}
            <div className="flex gap-2">
              <button
                data-testid="resume-session-btn"
                onClick={resumeLastSession}
                className="flex-1 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-all shadow-sm"
              >
                Resume
              </button>
              <button
                data-testid="dismiss-resume-btn"
                onClick={dismissResume}
                className="px-4 py-2.5 rounded-xl text-sm text-muted-foreground hover:bg-muted/50 transition-all border border-border/30"
              >
                Start Fresh
              </button>
            </div>
          </div>
        )}

        {/* Unsent Drafts Section */}
        {!activeConv && messages.length === 0 && draftConvIds && draftConvIds.size > 0 && (
          <div data-testid="drafts-section" className="max-w-md w-full mx-auto">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-3.5 h-3.5 text-amber-500" />
              <span className="text-xs font-medium text-muted-foreground">Unsent Drafts</span>
            </div>
            <div className="space-y-1.5">
              {Array.from(draftConvIds).slice(0, 4).map(convId => {
                const conv = (conversations || []).find(c => c.id === convId);
                const draftText = typeof window !== "undefined" ? localStorage.getItem(`omni_draft_${convId}`) : "";
                if (!draftText) return null;
                return (
                  <button
                    key={convId}
                    data-testid={`draft-card-${convId}`}
                    onClick={() => selectConversation(convId)}
                    className="w-full flex items-start gap-2.5 p-2.5 rounded-xl border border-amber-200/40 dark:border-amber-800/30 bg-amber-50/50 dark:bg-amber-950/20 hover:bg-amber-50 dark:hover:bg-amber-950/30 transition-all text-left group"
                  >
                    <div className="w-7 h-7 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <FileText className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-foreground/80 truncate">
                        {conv?.title || "Conversation"}
                      </p>
                      <p className="text-[11px] text-muted-foreground line-clamp-1 italic">
                        &ldquo;{draftText.slice(0, 80)}{draftText.length > 80 ? "..." : ""}&rdquo;
                      </p>
                    </div>
                    <span className="text-[10px] text-amber-600 dark:text-amber-400 font-medium ui-label flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      Resume
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {messages.length === 0 && (
          <div className="flex flex-col items-center text-center pt-2 sm:pt-6 pb-2 px-3 sm:px-4 max-w-lg mx-auto">
            {/* Greeting — compact on mobile */}
            <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-primary/10 flex items-center justify-center mb-2 sm:mb-3">
              <Sparkles className="w-5 h-5 sm:w-7 sm:h-7 text-primary" strokeWidth={1.5} />
            </div>
            <h2 className="font-heading text-lg sm:text-2xl font-medium mb-0.5">
              {t("chat.hello")} {name}
            </h2>
            <p className="text-muted-foreground text-xs sm:text-sm mb-3 sm:mb-4 max-w-xs">
              {t("chat.welcome")}
            </p>

            {/* Voice Onboarding Card (first login only) */}
            {showVoiceOnboarding && (
              <div className="mb-3 w-full flex justify-center">
                <VoiceOnboarding onComplete={setShowVoiceOnboarding} />
              </div>
            )}

            {/* Quick Mood Check-in — ultra-compact */}
            <div data-testid="quick-mood-welcome" className="mb-3 sm:mb-4">
              <p className="text-[10px] sm:text-[11px] text-muted-foreground mb-1.5">How are you feeling?</p>
              <div className="flex items-center gap-1 sm:gap-2 justify-center">
                {MOOD_EMOJIS.map((m, i) => (
                  <button
                    key={i}
                    data-testid={`quick-mood-${m.label.toLowerCase()}`}
                    onClick={() => quickMoodLog(m)}
                    className={cn(
                      "flex flex-col items-center gap-0 sm:gap-0.5 p-0.5 sm:p-1.5 rounded-xl hover:bg-muted/50 transition-all active:scale-95",
                      moodNoteFor?.label === m.label && "bg-primary/10 ring-2 ring-primary/30 scale-105"
                    )}
                    title={m.label}
                  >
                    <span className="text-base sm:text-xl">{m.emoji}</span>
                    <span className="text-[7px] sm:text-[9px] text-muted-foreground ui-label leading-none">{m.label}</span>
                  </button>
                ))}
              </div>
              {moodNoteFor && (
                <div data-testid="mood-note-input-area" className="mt-2.5 max-w-xs mx-auto animate-in fade-in slide-in-from-bottom-2 duration-200">
                  <div className="flex items-center gap-1.5">
                    <input
                      data-testid="mood-note-input"
                      type="text"
                      value={moodNoteInput}
                      onChange={e => setMoodNoteInput(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && submitMoodWithNote()}
                      placeholder="Add a note..."
                      className="flex-1 input-omni border border-input h-8 text-xs px-2.5"
                      autoFocus
                    />
                    <button data-testid="mood-note-submit" onClick={submitMoodWithNote} className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:opacity-90">Save</button>
                    <button data-testid="mood-note-skip" onClick={skipMoodNote} className="px-2 py-1.5 rounded-lg text-xs text-muted-foreground hover:bg-muted/50">Skip</button>
                  </div>
                </div>
              )}
            </div>

            {/* Quick-Start Templates — categorized, scrollable */}
            <QuickStartTemplates applyTemplate={applyTemplate} />

            {/* Wisdom Council CTA — compact on mobile, expanded on desktop */}
            {onOpenWisdomCouncil && (
              <button
                data-testid="wisdom-council-cta"
                onClick={onOpenWisdomCouncil}
                className="w-full mt-2.5 sm:mt-3 p-2.5 sm:p-3.5 rounded-xl sm:rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border border-amber-200/40 dark:border-amber-800/30 hover:shadow-lg hover:scale-[1.01] active:scale-[0.99] transition-all text-left flex items-center gap-2.5 sm:gap-3"
              >
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0 shadow-sm">
                  <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-white" strokeWidth={1.5} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-semibold text-amber-800 dark:text-amber-300 leading-tight">Wisdom Council</p>
                  <p className="text-[10px] sm:text-[11px] text-amber-600/70 dark:text-amber-400/60 leading-tight truncate sm:whitespace-normal">Jesus, Marcus Aurelius, Einstein &amp; 19 more</p>
                </div>
              </button>
            )}

            {/* Custom Templates — compact */}
            {customTemplates.length > 0 && (
              <div className="w-full mt-2">
                <div className="grid grid-cols-2 gap-1.5">
                  {customTemplates.map(tpl => (
                    <div key={tpl.id} className="group flex items-center gap-1.5 p-2 rounded-xl border border-border/50 bg-card hover:shadow-sm transition-all min-w-0">
                      <button
                        data-testid={`custom-template-${tpl.id}`}
                        onClick={() => { setInput(tpl.text); inputRef.current?.focus(); }}
                        className="flex-1 text-left text-xs text-foreground/80 truncate"
                      >
                        {tpl.text}
                      </button>
                      <button
                        data-testid={`delete-template-${tpl.id}`}
                        onClick={() => deleteTemplate(tpl.id)}
                        className="opacity-0 group-hover:opacity-100 p-0.5 text-muted-foreground hover:text-destructive transition-all flex-shrink-0"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {showNewTemplate ? (
              <div data-testid="new-template-form" className="flex gap-1.5 items-center w-full mt-2">
                <input
                  data-testid="new-template-input"
                  value={newTemplateText}
                  onChange={e => setNewTemplateText(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") saveTemplate(); if (e.key === "Escape") setShowNewTemplate(false); }}
                  placeholder="Type your custom prompt..."
                  className="flex-1 text-xs bg-muted/30 rounded-lg px-2.5 py-1.5 border border-border/40 focus:outline-none focus:border-primary/40"
                  autoFocus
                />
                <button data-testid="save-template-btn" onClick={saveTemplate} className="px-2.5 py-1.5 rounded-lg text-xs font-medium bg-primary text-primary-foreground">Save</button>
                <button onClick={() => { setShowNewTemplate(false); setNewTemplateText(""); }} className="px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground">Cancel</button>
              </div>
            ) : (
              <button
                data-testid="add-template-btn"
                onClick={() => setShowNewTemplate(true)}
                className="flex items-center gap-1.5 text-[11px] sm:text-xs text-muted-foreground hover:text-primary transition-colors mt-1.5 sm:mt-2"
              >
                <PlusCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5" strokeWidth={1.5} />
                Save a custom prompt
              </button>
            )}
          </div>
        )}

        {/* Pinned Messages Section */}

        {/* Calendar Widget — Activity Timeline on welcome screen */}
        {messages.length === 0 && (
          <div className="max-w-md mx-auto mt-3 px-3 sm:px-0">
            <CalendarWidget />
          </div>
        )}

        {/* Chat Messages */}
        {activeConv && pinnedMsgs.length > 0 && (
          <div className="mb-3">
            <button
              data-testid="pinned-messages-toggle"
              onClick={() => setShowPinned(!showPinned)}
              className="flex items-center gap-2 text-xs text-primary font-medium px-3 py-1.5 rounded-lg bg-primary/5 hover:bg-primary/10 transition-colors w-fit"
            >
              <Pin className="w-3.5 h-3.5" strokeWidth={2} />
              {pinnedMsgs.length} pinned message{pinnedMsgs.length !== 1 ? "s" : ""}
              <ChevronDown className={cn("w-3 h-3 transition-transform", showPinned && "rotate-180")} />
            </button>
            {showPinned && (
              <div data-testid="pinned-messages-list" className="mt-2 space-y-1.5 animate-in fade-in slide-in-from-top-1 duration-200">
                {pinnedMsgs.map(pm => (
                  <div
                    key={pm.index}
                    data-testid={`pinned-msg-${pm.index}`}
                    className="flex items-start gap-2 px-3 py-2 rounded-xl bg-primary/[0.03] border border-primary/10 cursor-pointer hover:bg-primary/[0.06] transition-colors"
                    onClick={() => {
                      const el = document.querySelector(`[data-testid="msg-${pm.role}-${pm.index}"]`);
                      if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("ring-2", "ring-primary/50"); setTimeout(() => el.classList.remove("ring-2", "ring-primary/50"), 2000); }
                    }}
                  >
                    <Pin className="w-3 h-3 text-primary mt-0.5 flex-shrink-0" strokeWidth={2} />
                    <div className="min-w-0">
                      <span className="text-[10px] text-muted-foreground capitalize">{pm.role}</span>
                      <p className="text-xs truncate">{pm.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={cn("flex", msg.role === "user" ? "justify-end" : "justify-start")}>
            <div className="group relative max-w-[80%]">
              {/* Roundtable figure attribution */}
              {msg.isRoundtable && msg.wisdom_figure_name && (
                <div className="flex items-center gap-1.5 mb-1 pl-1">
                  <div className="w-5 h-5 rounded-full bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center">
                    <span className="text-[9px] font-bold text-violet-600 dark:text-violet-300">{msg.wisdom_figure_name.charAt(0)}</span>
                  </div>
                  <span className="text-[11px] font-semibold text-violet-600 dark:text-violet-300">{msg.wisdom_figure_name}</span>
                </div>
              )}
              <div
                data-testid={`msg-${msg.role}-${i}`}
                className={cn(
                  msg.role === "user" ? "chat-bubble-user" : "chat-bubble-ai",
                  msg.isRoundtable && "border-l-2 border-violet-400/40"
                )}
              >
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
              </div>
              {msg.role === "assistant" && (
                <>
                  <ReactionBar
                    conversationId={activeConv}
                    messageIndex={i}
                    existingReactions={reactionsMap[String(i)] || {}}
                    onReactionChange={handleReactionChange}
                  />
                  <div className="flex items-center gap-0.5 sm:gap-1 mt-1 sm:mt-0 sm:absolute sm:-bottom-2 sm:right-2 sm:opacity-0 sm:group-hover:opacity-100 transition-all duration-200 max-w-full">
                    <MessageSpeaker text={msg.content} msgId={`msg-${i}`} voiceOverride={msg.wisdom_voice || activeWisdomFigure?.voice} />
                    <button
                      data-testid={`bookmark-msg-${i}`}
                      onClick={() => toggleBookmark(i)}
                      className={cn(
                        "bg-card border border-border/50 rounded-full px-2.5 py-1.5 flex items-center gap-1 text-xs shadow-sm transition-all",
                        bookmarkedMsgs[String(i)]
                          ? "text-primary border-primary/30"
                          : "text-muted-foreground hover:text-primary hover:border-primary/30"
                      )}
                      title={bookmarkedMsgs[String(i)] ? "Remove bookmark" : "Bookmark"}
                    >
                      {bookmarkedMsgs[String(i)] ? <BookmarkCheck className="w-3 h-3" /> : <Bookmark className="w-3 h-3" />}
                    </button>
                    <button
                      data-testid={`pin-msg-${i}`}
                      onClick={() => togglePinMessage(i)}
                      className={cn(
                        "bg-card border border-border/50 rounded-full px-2.5 py-1.5 flex items-center gap-1 text-xs shadow-sm transition-all",
                        pinnedMsgs.some(p => p.index === i)
                          ? "text-primary border-primary/30"
                          : "text-muted-foreground hover:text-primary hover:border-primary/30"
                      )}
                      title={pinnedMsgs.some(p => p.index === i) ? "Unpin" : "Pin to top"}
                    >
                      <Pin className="w-3 h-3" strokeWidth={1.5} />
                    </button>
                    <button
                      data-testid={`share-single-msg-${i}`}
                      onClick={() => setShareMsg({ content: msg.content, module, wisdomFigure: activeWisdomFigure?.name })}                      className="bg-card border border-border/50 rounded-full px-2.5 py-1.5 flex items-center gap-1 text-xs text-muted-foreground hover:text-primary hover:border-primary/30 shadow-sm transition-all"
                    >
                      <Share2 className="w-3 h-3" />
                      <span className="hidden sm:inline">{t("chat.share")}</span>
                    </button>
                  </div>
                </>
              )}
              {msg.role === "user" && (
                <div className="flex items-center gap-0.5 mt-1 sm:mt-0 sm:absolute sm:-bottom-2 sm:left-2 sm:opacity-0 sm:group-hover:opacity-100 transition-all duration-200">
                  <button
                    data-testid={`bookmark-msg-${i}`}
                    onClick={() => toggleBookmark(i)}
                    className={cn(
                      "bg-card border border-border/50 rounded-full px-2.5 py-1.5 flex items-center gap-1 text-xs shadow-sm transition-all",
                      bookmarkedMsgs[String(i)]
                        ? "text-primary border-primary/30"
                        : "text-muted-foreground hover:text-primary hover:border-primary/30"
                    )}
                    title={bookmarkedMsgs[String(i)] ? "Remove bookmark" : "Bookmark"}
                  >
                    {bookmarkedMsgs[String(i)] ? <BookmarkCheck className="w-3 h-3" /> : <Bookmark className="w-3 h-3" />}
                  </button>
                  <button
                    data-testid={`pin-msg-user-${i}`}
                    onClick={() => togglePinMessage(i)}
                    className={cn(
                      "bg-card border border-border/50 rounded-full px-2.5 py-1.5 flex items-center gap-1 text-xs shadow-sm transition-all",
                      pinnedMsgs.some(p => p.index === i)
                        ? "text-primary border-primary/30"
                        : "text-muted-foreground hover:text-primary hover:border-primary/30"
                    )}
                    title={pinnedMsgs.some(p => p.index === i) ? "Unpin" : "Pin to top"}
                  >
                    <Pin className="w-3 h-3" strokeWidth={1.5} />
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}

        {sending && (
          <div className="flex justify-start">
            <div className="chat-bubble-ai flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">
                {bulkProgress
                  ? `Answering ${bulkProgress.current} of ${bulkProgress.total}...`
                  : "Thinking..."}
              </span>
            </div>
          </div>
        )}

        {/* Smart Reply Suggestions */}
        {!sending && smartReplies.length > 0 && messages.length > 0 && (
          <div data-testid="smart-replies" className="flex flex-wrap gap-2 justify-start animate-in fade-in slide-in-from-bottom-2 duration-300">
            {smartReplies.map((reply, i) => (
              <button
                key={i}
                data-testid={`smart-reply-${i}`}
                onClick={() => sendSmartReply(reply)}
                className="px-3 py-1.5 text-xs rounded-full border border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 hover:border-primary/50 transition-all active:scale-95"
              >
                {reply}
              </button>
            ))}
          </div>
        )}
        {loadingReplies && !sending && (
          <div className="flex gap-2">
            {[0,1,2].map(i => (
              <div key={i} className="h-7 w-24 rounded-full bg-muted/40 animate-pulse" />
            ))}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Zoom level indicator */}
      {zoomLevel > 1.1 && (
        <div className="absolute top-16 right-4 z-20 animate-in fade-in duration-200">
          <button
            data-testid="zoom-reset-btn"
            onClick={() => setZoomLevel(1)}
            className="px-3 py-1.5 rounded-full bg-card/90 backdrop-blur-sm border border-border/50 shadow-lg text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            {Math.round(zoomLevel * 100)}% — tap to reset
          </button>
        </div>
      )}
    </>
  );
}
